import React, { useState, useEffect, useRef } from "react"; 
import { SidebarItem } from "../../types/sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import * as Icons from "../../assets/SVGs/index"; 
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../state-management/store";
import { selectGlobalState } from "../../state-management/selectors";
import { getRoles } from "../../state-management/actions/role-actions";
import { Tooltip } from "react-tooltip"; 

interface RecursiveMenuProps {
  items: SidebarItem[];
  level: number;
  parentIndex: number[];
  isCollapsed: boolean;
  submenuBgColor: string;
}

const filterMenuItemsByPermissions = (
  items: SidebarItem[],
  permissions: string[]
): SidebarItem[] =>
  items
    .map((item: SidebarItem) => {
      const filteredSubNav = item.subNav
        ? filterMenuItemsByPermissions(item.subNav, permissions)
        : [];
      // Include item if it has subNav or path is in permissions (or no path check needed)
      const shouldInclude =
        filteredSubNav.length > 0 ||
        (item.path && permissions?.includes(item.path)) ||
        true; // Always include by default unless filtered elsewhere
      if (shouldInclude) {
        const newItem = { ...item };
        if (filteredSubNav.length > 0) newItem.subNav = filteredSubNav;
        else delete newItem.subNav;
        return newItem;
      }
      return null; // Only filter out if explicitly unwanted
    })
    .filter(Boolean) as SidebarItem[];

const RecursiveMenu: React.FC<RecursiveMenuProps> = ({
  items,
  level,
  parentIndex,
  isCollapsed,
  submenuBgColor,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const themeMode = useSelector((state: RootState) => state.globalState.theme);
  const { currentUserData, rolesData } = useSelector(selectGlobalState);

  const [expandedSubmenu, setExpandedSubmenu] = useState<string[]>([]);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    dispatch(getRoles());
  }, [dispatch]);

  const filteredItems = React.useMemo(() => {
    const getUserPermissions = () => {
      const perms = rolesData?.find(
        (item: any) => item._id === currentUserData.role_id
      )?.permissions || [];
      console.log("Permissions:", perms);
      return perms;
    };
    const filtered = filterMenuItemsByPermissions(items, getUserPermissions());
    console.log("Filtered Items:", filtered);
    return filtered;
  }, [rolesData, currentUserData, items]);

  const handleNavigate = (path: string) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
  };

  const handleAccordionToggle = (key: string) => {
    setExpandedSubmenu((prev) => {
      if (isCollapsed) {
        return prev.includes(key) ? [] : [key];
      } else {
        return prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key];
      }
    });
  };

  const buildIndexPath = (parentIndexPath: number[], currentIndex: number) => [
    ...parentIndexPath,
    currentIndex,
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isMobile = window.innerWidth <= 768;
      const isCollapsedMode = isCollapsed || isMobile;

      if (isCollapsedMode && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setExpandedSubmenu([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCollapsed]);

  useEffect(() => {
    if (isCollapsed) {
      setExpandedSubmenu([]);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (!filteredItems.length) return;
  
    const currentPath = location.pathname;
    setExpandedSubmenu((prev) => {
      const newExpanded = [...prev];
  
      filteredItems.forEach((item) => {
        if (item.subNav?.some((subItem) => subItem.path === currentPath)) {
          if (!newExpanded.includes(item.name)) newExpanded.push(item.name);
        }
      });
  
      return newExpanded;
    });
  }, [location.pathname, filteredItems]);

  return (
    <ul
      ref={menuRef}
      className={`text-white ${
        level === 1 && themeMode === "light"
          ? "bg-[#013537] shadow"
          : level === 1 && themeMode === "dark"
          ? "bg-[#272a2f] shadow"
          : ""
      } h-full p-1 rounded-lg flex flex-col gap-y-3 transition-all`}
    >
      {filteredItems.map((item: SidebarItem, index: number) => {
        const indexPath = buildIndexPath(parentIndex, index);
        const key = indexPath.join("-");
        const SvgIcon = item.icon ? Icons[item.icon as keyof typeof Icons] : null;
        const hasSubNav = item.subNav && item.subNav.length > 0;
        const isExpanded = expandedSubmenu.includes(item.name);

        return (
          <li key={key} className="relative">
            <button
              className={`rounded-md my-1 cursor-pointer py-1 items-start ${
                location.pathname === item.path ? "bg-[#272a2f] shadow border border-[#676769]" : ""
              } ${isCollapsed ? "px-0" : "px-5"} text-right w-full border border-[#ffffff00] flex items-center ${
                isCollapsed ? "justify-evenly" : "justify-between"
              } transition-all duration-200 ease-in-out hover:bg-[#272a2f] hover:shadow`}
              onClick={() => {
                if (hasSubNav) handleAccordionToggle(item.name);
                else handleNavigate(item.path || "");
              }}
              data-tooltip-id={key}
              data-tooltip-content={isCollapsed ? item.title : ""}
            >
              <div className="flex items-center gap-x-3">
                {SvgIcon && <SvgIcon className="w-6 h-6 text-white" />}
                {!isCollapsed && <span className="text-xs font-medium">{item.title}</span>}
              </div>

              {hasSubNav && !isCollapsed && (
                <Icons.ArrowDownIcon
                  className={`text-white transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
              )}
              
            </button>
            <Tooltip
              id={key}
              place="right"
              className="hidden md:block"
              style={{
                direction: "rtl",
                textAlign: "right",
                position: "fixed",
                right: "100%",
                zIndex: "100",
              }}
            />
            {hasSubNav && isExpanded && (
              <ul
                style={{ backgroundColor: submenuBgColor }}
                className={`flex flex-col gap-y-2 transition-all duration-300 shadow ${
                  isCollapsed ? "fixed right-full top-0 w-48 z-50 h-full px-1 pt-[45px]" : "h-auto"
                }`}
              >
                {item.subNav?.map((subItem, subIndex) => {
                  const SubSvgIcon = subItem.icon && Icons[subItem.icon as keyof typeof Icons];
                  return (
                    <li key={`${key}-${subIndex}`}>
                      <button
                        onClick={() => handleNavigate(subItem.path || "")}
                        className={`text-white w-full text-right md:pl-0 px-2 ${
                          isCollapsed ? "md:pr-1" : "md:pr-8"
                        } py-2 rounded-md hover:bg-[#272a2f] hover:shadow ${
                          location.pathname === subItem.path ? "bg-[#272a2f] shadow border border-[#676769]" : ""
                        }`}
                      >
                        <div className="flex items-center gap-x-3 md:text-xs text-[12px]">
                          {SubSvgIcon && <SubSvgIcon className="w-5 h-5 text-white" />}
                          {subItem.title}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default RecursiveMenu;
